// services/InitializeService.ts
import axios, { AxiosInstance } from "axios";
import {
  PublicClientApplication,
  InteractionRequiredAuthError,
} from "@azure/msal-browser"; // Import MSAL types
import { config } from "../Utility/Constants";

const apiBaseURL = config.url.API_URL;

// ✅ SHARED AXIOS INSTANCE - Used by all domain services
export const sharedAxiosInstance: AxiosInstance = axios.create({
  baseURL: apiBaseURL,
  timeout: 120000,
});

let msalInstance: PublicClientApplication | null = null; // Declare msalInstance as nullable

// Helper function to check if error requires interaction
const isInteractionRequiredError = (error: any): boolean => {
  return (
    error instanceof InteractionRequiredAuthError ||
    error.errorCode === "interaction_required" ||
    error.errorCode === "consent_required" ||
    error.errorCode === "login_required" ||
    error.errorCode === "token_expired"
  );
};

// Helper function to initiate authentication flow with interaction state checking
const initiateAuthFlow = async (scopes: string[]) => {
  if (!msalInstance) {
    throw new Error("MSAL instance not available for authentication");
  }

  try {
    // Check if there's already an interaction in progress
    const activeRequest = (
      msalInstance as any
    ).browserStorage?.getInteractionInProgress();
    if (activeRequest) {
      console.info(
        "Authentication interaction already in progress, waiting...",
      );
      // Return a promise that rejects to indicate auth is needed but in progress
      throw new Error("Authentication in progress - please wait");
    }

    await msalInstance.loginRedirect({ scopes });
  } catch (error: any) {
    if (error.errorCode === "interaction_in_progress") {
      console.info("Authentication interaction already in progress");
      throw new Error("Authentication in progress - please wait");
    }
    console.error("Failed to initiate authentication flow:", error);
    throw new Error("Authentication flow initiation failed");
  }
};
export const initializeService = (msalApp: PublicClientApplication) => {
  msalInstance = msalApp; // Assign the initialized msal instance

  sharedAxiosInstance.interceptors.request.use(
    async function (config) {
      // Made the function async
      if (!msalInstance) {
        throw new Error("MSAL instance not initialized for service.");
      }

      const activeAccount = msalInstance.getActiveAccount();
      const accounts = msalInstance.getAllAccounts();

      if (!activeAccount && accounts.length === 0) {
        // Handle unauthenticated state appropriately
        console.warn(
          "No active MSAL account found. User is not authenticated.",
        );

        // Check if authentication is already in progress at the app level
        try {
          const activeRequest = (
            msalInstance as any
          ).browserStorage?.getInteractionInProgress();
          if (activeRequest) {
            console.info(
              "Authentication already in progress at app level, skipping API call",
            );
            return Promise.reject(
              new Error(
                "Authentication in progress - API call will retry after login",
              ),
            );
          }
        } catch (checkError) {
          // Ignore errors checking interaction state
        }

        // Don't initiate auth flow here - let the app-level authentication handle it
        console.info("Deferring to app-level authentication flow");
        return Promise.reject(
          new Error("Authentication required - handled by app level"),
        );
      }

      const accessTokenRequest = {
        scopes: ["api://fdc9c7eb-6429-4eec-a9b9-eb67d61aebfa/access_as_user"],
        account: activeAccount || accounts[0],
      };

      try {
        const accessTokenResponse =
          await msalInstance.acquireTokenSilent(accessTokenRequest);
        config.headers.Authorization = `Bearer ${accessTokenResponse.accessToken}`;
        return config;
      } catch (error: any) {
        console.error("Error acquiring token silently:", error);

        // Handle specific MSAL errors that require interaction
        if (isInteractionRequiredError(error)) {
          console.info(
            "Interactive authentication required. Checking for ongoing interactions...",
          );

          // Check if interaction is already in progress before attempting
          try {
            const activeRequest = (
              msalInstance as any
            ).browserStorage?.getInteractionInProgress();
            if (activeRequest) {
              console.info(
                "Authentication interaction already in progress, skipping token request",
              );
              return Promise.reject(
                new Error(
                  "Authentication in progress - token will be acquired after login",
                ),
              );
            }
          } catch (checkError) {
            // Ignore errors checking interaction state
          }

          // Attempt interactive token acquisition only if no interaction is in progress
          try {
            const scopes = [
              "api://fdc9c7eb-6429-4eec-a9b9-eb67d61aebfa/access_as_user",
            ];
            await initiateAuthFlow(scopes);
            // This will redirect, so the code below won't execute
            return Promise.reject(
              new Error("Interactive authentication required - redirecting"),
            );
          } catch (redirectError: any) {
            if (redirectError.message?.includes("Authentication in progress")) {
              return Promise.reject(
                new Error("Authentication in progress - please wait"),
              );
            }
            console.error(
              "Failed to initiate interactive token acquisition:",
              redirectError,
            );
            return Promise.reject(new Error("Token acquisition failed"));
          }
        }

        // For other errors, reject the promise to let the calling component handle it
        return Promise.reject(error);
      }
    },
    function (error) {
      return Promise.reject(error);
    },
  );

  sharedAxiosInstance.interceptors.response.use(
    (response) => {
      return response;
    },
    async (error) => {
      // Handle 401 Unauthorized responses
      if (error.response?.status === 401) {
        console.warn(
          "Received 401 Unauthorized response. Attempting to refresh authentication...",
        );

        if (msalInstance) {
          const activeAccount = msalInstance.getActiveAccount();
          const accounts = msalInstance.getAllAccounts();

          if (activeAccount || accounts.length > 0) {
            // Try to acquire a new token silently first
            try {
              const accessTokenRequest = {
                scopes: [
                  "api://fdc9c7eb-6429-4eec-a9b9-eb67d61aebfa/access_as_user",
                ],
                account: activeAccount || accounts[0],
              };

              const tokenResponse =
                await msalInstance.acquireTokenSilent(accessTokenRequest);
              // Retry the original request with the new token
              if (error.config && tokenResponse.accessToken) {
                error.config.headers.Authorization = `Bearer ${tokenResponse.accessToken}`;
                return sharedAxiosInstance.request(error.config);
              }
            } catch (tokenError) {
              console.error("Failed to refresh token silently:", tokenError);

              // If silent refresh fails and it requires interaction, redirect to interactive login
              if (isInteractionRequiredError(tokenError)) {
                try {
                  const scopes = [
                    "api://fdc9c7eb-6429-4eec-a9b9-eb67d61aebfa/access_as_user",
                  ];
                  await initiateAuthFlow(scopes);
                } catch (loginError) {
                  console.error(
                    "Failed to initiate login redirect:",
                    loginError,
                  );
                  // Fallback: redirect to logout page
                  window.location.href = "/logout";
                }
              } else {
                // For other token errors, also fallback to logout
                console.error(
                  "Token refresh failed with non-interaction error:",
                  tokenError,
                );
                window.location.href = "/logout";
              }
            }
          } else {
            // No accounts available, redirect to login
            console.warn(
              "No active account found for token refresh. Redirecting to login...",
            );
            try {
              const scopes = [
                "api://fdc9c7eb-6429-4eec-a9b9-eb67d61aebfa/access_as_user",
              ];
              await initiateAuthFlow(scopes);
            } catch (loginError) {
              console.error("Failed to initiate login redirect:", loginError);
              // Fallback: redirect to logout page
              window.location.href = "/logout";
            }
          }
        } else {
          // MSAL instance not available, fallback to logout
          console.error(
            "MSAL instance not available for token refresh. Redirecting to logout...",
          );
          window.location.href = "/logout";
        }
      }

      return Promise.reject(error);
    },
  );
};

const getWithAuth = (url: string, config?: any) => {
  return sharedAxiosInstance.get(url, config);
};

const postWithAuth = (url: string, data: any, config?: any) => {
  return sharedAxiosInstance.post(`${apiBaseURL}/${url}`, data, config);
};

const putWithAuth = (url: string, data: any, config?: any) => {
  return sharedAxiosInstance.put(`${apiBaseURL}/${url}`, data, config);
};

const deleteWithAuth = (url: string, config?: any) => {
  return sharedAxiosInstance.delete(`${apiBaseURL}/${url}`, config);
};

export { getWithAuth, postWithAuth, putWithAuth, deleteWithAuth };

// /**
//  * Shared Axios instance with MSAL authentication interceptors
//  * Used by all domain service files (spokes)
//  */

// const axiosInstance = axios.create({
//   baseURL: config.url.API_URL,
//   timeout: 120000,
// });

// // Request interceptor: Add MSAL Bearer token
// axiosInstance.interceptors.request.use(
//   function (config) {
//     const instance = msalInstance;
//     const activeAccount = msalInstance.getActiveAccount();
//     const accounts = instance.getAllAccounts();

//     if (!activeAccount && accounts.length === 0) {
//       /** User is not signed in. Throw error or wait for user to login.
//        * Do not attempt to log a user in outside of the context of MsalProvider
//        */
//     }

//     const accessTokenRequest = {
//       scopes: ["api://fdc9c7eb-6429-4eec-a9b9-eb67d61aebfa/access_as_user"],
//       account: activeAccount || accounts[0],
//     };

//     return instance
//       .acquireTokenSilent(accessTokenRequest)
//       .then((accessTokenResponse) => {
//         // Acquire token silent success
//         let accessToken = accessTokenResponse.accessToken;
//         // Call your API with token
//         config.headers.Authorization = `Bearer ${accessToken}`;
//         return Promise.resolve(config);
//       });
//   },
//   function (error) {
//     return Promise.reject(error);
//   },
// );

// // Response interceptor: Handle 401 errors
// axiosInstance.interceptors.response.use(
//   (response) => {
//     if (response.status === 401) {
//       // Clear local storage, redirect back to login
//       window.location.href = "/logout";
//     }
//     return response;
//   },
//   (error) => {
//     return Promise.reject(error);
//   },
// );

// export default axiosInstance;
